import { Tag } from '@components/UI-Components/Tag'
import { DropTarget } from '@components/Utils/DropTarget'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import { useObserver } from 'mobx-react-lite'
import React, { useRef, useState } from 'react'
import * as css from './BulkOperationTabContent.css'
import { useTranslation } from 'react-i18next'
import { CustomPreviewDraggableSource } from '@components/Utils/CustomPreviewDraggableSource'
import classNames from 'classnames'
import SelectionByMouseDrag from '@components/Utils/SelectionByMouseDrag'
import { DraggableSource } from '@components/Utils/DraggableSource'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { ListViewFieldMapping } from '@modules/common/models/enums/ListViewFieldMapping'
import { Tooltip } from '@material-ui/core'
import { SelectAllNewIcon } from '@components/UI-Components/svg'
import { CancelRounded } from '@material-ui/icons'

interface IRightSideBulkOperationTabContent {
  rightSideSelectedChipsForDrag: any[]
  selectedEntities: any[]
  bulkOpSelectedTab: string
  handleChipsDeselection: Function
  handleChipsSelectionByMouseDrag: any
  setIsWindowSelectionGoingOn: any
  mouseDownHandlerForChips: any
  onMouseUpHandlerOnChip: any
  addBulkOpSelectedEntity: any
  removeBulkOpEntity: any
  handleTagClick: any
  onSelectAllTags: any
}

export const RightSideBulkOperationTabContent: React.FunctionComponent<
  IRightSideBulkOperationTabContent
> = ({
  rightSideSelectedChipsForDrag,
  selectedEntities,
  bulkOpSelectedTab,
  handleChipsDeselection,
  handleChipsSelectionByMouseDrag,
  setIsWindowSelectionGoingOn,
  mouseDownHandlerForChips,
  onMouseUpHandlerOnChip,
  addBulkOpSelectedEntity,
  removeBulkOpEntity,
  handleTagClick,
  onSelectAllTags
}) => {
  return useObserver(function useHook() {
    const { t } = useTranslation()
    const rightSideItemsRef = useRef([])
    const rightSideContainerRef = useRef(null) // Reference to the container element
    const [isMultipleDragActive, setIsMultipleDragActive] = useState(false)
    const [windowSelectionHeaderPosition, setWindowSelectionHeaderPosition] = useState(0)
    const HEADER_OFFSET_HEIGHT = 35
    const customDraggableChildren = (
      <div className={css.customDraggableEntitySection}>
        {rightSideSelectedChipsForDrag.map((entity, index) => {
          const clusterEntity = 'RGBHex' in entity
          return (
            <Chip
              key={`entity_${index}`}
              className={classNames(css.entityChip, {
                [css.clusterChip]: clusterEntity,
                [css.selectedChip]: true
              })}
              classes={{
                label: clusterEntity ? css.clusterChipLabel : css.entityChipLabel
              }}
              label={
                <Tag
                  className={css.circle}
                  key={`entityLabel_${index}`}
                  color={clusterEntity ? entity.RGBHex : null}
                  name={entity.Description || entity.Name}
                  tooltipPlacement={'bottom-start'}
                />
              }
            />
          )
        })}
      </div>
    )
    const handleMouseDownEvent = entity => {
      if (
        rightSideSelectedChipsForDrag?.length &&
        rightSideSelectedChipsForDrag?.some(ele => ele?.Id === entity?.Id)
      ) {
        setIsMultipleDragActive(true)
      } else {
        setIsMultipleDragActive(false)
      }
    }

    return (
      <DropTarget
        className={css.dropTarget}
        canDrop
        handleDrop={({
          type,
          selectedChipsForDrag,
          isItemDrag,
          item,
          isRightHandSideDrag
        }) => {
          if (!isRightHandSideDrag) {
            const items = isItemDrag ? [item] : selectedChipsForDrag
            addBulkOpSelectedEntity(type, items)
          }
        }}
      >
        <div
          onClick={() => {
            handleChipsDeselection(true)
          }}
        >
          <SelectionByMouseDrag
            items={selectedEntities}
            itemsRef={rightSideItemsRef}
            handleSelectionByMouseDrag={handleChipsSelectionByMouseDrag}
            canDragAndDrop={false}
            className={css.windowSelectContainer}
            containerRef={rightSideContainerRef}
            setIsWindowSelectionGoingOn={setIsWindowSelectionGoingOn}
            setWindowSelectionHeaderPosition={setWindowSelectionHeaderPosition}
            windowSelectionHeaderPosition={windowSelectionHeaderPosition}
            headerOffsetHeight={HEADER_OFFSET_HEIGHT}
            isRightHandSideOperation={true}
          >
            <div
              className={css.headerWrapper}
              onMouseDown={e => {
                e.preventDefault()
                const containerPosition =
                  rightSideContainerRef?.current?.getBoundingClientRect()
                const clickPosition = Math.round(e?.clientY - containerPosition?.y)
                setWindowSelectionHeaderPosition(clickPosition)
              }}
            >
              <Typography className={css.dragSectionHeader}>
                {t(
                  'bb.common.bulkOpDragSectionHeaders.draggedSectionHeader',
                  'Apply to selected products'
                )}
              </Typography>
              <Tooltip title={t('common.label.selectAll', 'Select All')}>
                <div className={css.iconWrapper}>
                  <img
                    src={SelectAllNewIcon}
                    alt='Select all'
                    className={classNames(css.icon, {
                      [css.disabledIcon]:
                        selectedEntities?.length ===
                          rightSideSelectedChipsForDrag?.length ||
                        !selectedEntities?.length
                    })}
                    onClick={e => {
                      e.stopPropagation()
                      if (
                        selectedEntities?.length &&
                        selectedEntities?.length !== rightSideSelectedChipsForDrag?.length
                      ) {
                        onSelectAllTags(selectedEntities, true)
                      }
                    }}
                  />
                </div>
              </Tooltip>
            </div>
            <CustomPreviewDraggableSource
              key={bulkOpSelectedTab}
              className={css.draggbleSource}
              canDrag={isMultipleDragActive}
              libraryItem={{
                id: bulkOpSelectedTab,
                type: bulkOpSelectedTab,
                selectedChipsForDrag: rightSideSelectedChipsForDrag,
                isRightHandSideDrag: true
              }}
              customDraggableChildren={customDraggableChildren}
              containerRef={rightSideContainerRef}
            >
              <div className={css.draggableEntitySection}>
                {selectedEntities?.map((entity, index) => {
                  const selectedEntity = rightSideSelectedChipsForDrag?.find(
                    ele => ele?.Id === entity?.Id
                  )
                  const clusterEntity = 'RGBHex' in entity
                  return (
                    <DraggableSource
                      key={entity?.Id}
                      canDrag={!isMultipleDragActive}
                      libraryItem={{
                        id: entity?.Id,
                        type: bulkOpSelectedTab,
                        isItemDrag: true,
                        item: entity,
                        isRightHandSideDrag: true
                      }}
                    >
                      <div
                        className={css.chipWrapper}
                        ref={el => (rightSideItemsRef.current[index] = el)}
                        onMouseDown={e => {
                          handleMouseDownEvent(entity)
                          mouseDownHandlerForChips(e, entity, selectedEntities, true)
                        }}
                        onMouseUp={e => {
                          if (!(clusterEntity && VIPChannel.Retail === entity.Channel)) {
                            onMouseUpHandlerOnChip(e, entity, true)
                          }
                        }}
                      >
                        <Chip
                          key={`entityRight_${index}`}
                          className={classNames(css.entityChip, {
                            [css.clusterChip]: clusterEntity,
                            [css.selectedChip]: !!selectedEntity?.Id
                          })}
                          classes={{
                            label: css.entityChipLabel
                          }}
                          label={
                            <Tag
                              className={css.circle}
                              key={`entityLabelRight_${index}`}
                              color={clusterEntity ? entity.RGBHex : null}
                              name={entity.Description || entity.Name}
                              tooltipPlacement={'bottom'}
                              clusterStatus={entity.clusterStatus}
                            />
                          }
                          deleteIcon={
                            <CancelRounded
                              onMouseUp={event => {
                                event.stopPropagation()
                                removeBulkOpEntity(
                                  bulkOpSelectedTab,
                                  entity.Value || entity.Id
                                )
                              }}
                            />
                          }
                          onDelete={() => {}}
                          onClick={event => {
                            if (clusterEntity && VIPChannel.Retail === entity.Channel) {
                              event.stopPropagation()
                              if (
                                !(event?.shiftKey || event?.ctrlKey || event?.metaKey)
                              ) {
                                handleTagClick(
                                  null,
                                  entity.Id,
                                  ListViewFieldMapping.RetailClusters,
                                  event,
                                  true
                                )
                              }
                            }
                          }}
                        />
                      </div>
                    </DraggableSource>
                  )
                })}
              </div>
            </CustomPreviewDraggableSource>
          </SelectionByMouseDrag>
        </div>
      </DropTarget>
    )
  })
}
